import React from 'react';
import { createPortal } from 'react-dom';
import i18n from 'i18next';

interface SpinnerOverlayProps {
  message?: string;
  // Lets the page underneath show through instead of covering it with a solid background.
  transparent?: boolean;
}

// Decorative Chumash text (Bereshit) printed on the pages — one entry per page.
const PAGES: string[][] = [
  [
    'בראשית ברא אלהים',
    'את השמים ואת הארץ',
    'והארץ היתה תהו ובהו',
    'וחשך על פני תהום',
    'ורוח אלהים מרחפת',
    'על פני המים',
  ],
  [
    'ויאמר אלהים יהי אור',
    'ויהי אור וירא אלהים',
    'את האור כי טוב',
    'ויבדל אלהים בין האור',
    'ובין החשך ויקרא',
    'אלהים לאור יום',
  ],
  [
    'ויאמר אלהים יהי רקיע',
    'בתוך המים ויהי',
    'מבדיל בין מים למים',
    'ויעש אלהים את הרקיע',
    'ויבדל בין המים',
    'אשר מתחת לרקיע',
  ],
  [
    'ויאמר אלהים יקוו המים',
    'מתחת השמים אל מקום',
    'אחד ותראה היבשה',
    'ויהי כן ויקרא אלהים',
    'ליבשה ארץ ולמקוה',
    'המים קרא ימים',
  ],
  [
    'ויאמר אלהים תדשא',
    'הארץ דשא עשב מזריע',
    'זרע עץ פרי עשה פרי',
    'למינו אשר זרעו בו',
    'על הארץ ויהי כן',
    'ותוצא הארץ דשא',
  ],
  [
    'ויאמר אלהים יהי מארת',
    'ברקיע השמים להבדיל',
    'בין היום ובין הלילה',
    'והיו לאתת ולמועדים',
    'ולימים ושנים',
    'והיו למאורת ברקיע',
  ],
  [
    'ויאמר אלהים ישרצו',
    'המים שרץ נפש חיה',
    'ועוף יעופף על הארץ',
    'ויברא אלהים את',
    'התנינם הגדלים',
    'ואת כל נפש החיה',
  ],
  [
    'ויברך אתם אלהים',
    'לאמר פרו ורבו',
    'ומלאו את המים בימים',
    'והעוף ירב בארץ',
    'ויהי ערב ויהי בקר',
    'יום חמישי',
  ],
  [
    'ויאמר אלהים תוצא הארץ',
    'נפש חיה למינה בהמה',
    'ורמש וחיתו ארץ',
    'למינה ויהי כן',
    'ויעש אלהים את חית',
    'הארץ למינה',
  ],
  [
    'ויאמר אלהים נעשה אדם',
    'בצלמנו כדמותנו',
    'וירדו בדגת הים',
    'ובעוף השמים',
    'ויברא אלהים את האדם',
    'בצלמו בצלם אלהים',
  ],
];

// Stacked page edges: a few offset shadows give the book block its thickness.
const pageStack = (dx: number) =>
  Array.from(
    { length: 26 },
    (_, i) => `${dx * (i + 1) * 0.25}px ${(i + 1) * 0.5}px 0 ${i % 2 ? '#e6c999' : '#f7e4c4'}`,
  ).join(', ');

// Hebrew books open right-to-left: pages start on the left half and flip over to the right.
const bookStyles = `
  .kt-stage { perspective: 800px; }
  .kt-book { transform: rotateX(38deg); transform-style: preserve-3d; }
  .kt-page {
    background-color: #fbe8ca;
    background-image: linear-gradient(to var(--kt-spine), rgba(110, 60, 25, 0.32), transparent 38%);
    color: #5d4a3a;
    font-size: 9.5px;
    font-weight: 500;
    line-height: 1.6;
    direction: rtl;
    text-align: right;
    padding: 12px 14px 12px 18px;
    overflow: hidden;
    backface-visibility: hidden;
  }
  .kt-left { --kt-spine: right; }
  .kt-right { --kt-spine: left; }
  .kt-static.kt-left { box-shadow: ${pageStack(-1)}; }
  .kt-static.kt-right { box-shadow: ${pageStack(1)}; }
  .kt-leaf {
    transform-origin: right center;
    transform-style: preserve-3d;
    animation: kt-flip 2.4s ease-in-out infinite;
  }
  .kt-back { transform: rotateY(180deg); }
  .kt-bob { animation: kt-bob 2.4s ease-in-out infinite; }
  @keyframes kt-flip {
    0%, 6% { transform: translateZ(1px) rotateY(0deg); }
    40%, 100% { transform: translateZ(1px) rotateY(180deg); }
  }
  @keyframes kt-bob {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }
  @media (prefers-reduced-motion: reduce) {
    .kt-leaf, .kt-bob { animation: none; }
  }
`;

const pageText = (lines: string[]) =>
  lines.map((line) => (
    <div key={line} className="whitespace-nowrap">
      {line}
    </div>
  ));

const overlayContent = (message: string, transparent: boolean) => (
  <div
    className={`fixed inset-0 z-[2147483647] flex h-full min-h-screen w-full min-w-[100vw] items-center justify-center ${
      transparent ? 'bg-transparent' : 'bg-[var(--kol-surface)]'
    }`}
  >
    {transparent && <div className="absolute inset-0 bg-white/50" />}
    <div className="relative flex flex-col items-center ">
      {/* Same 55px footprint as the old ring; the full-size book is scaled down into it. */}
      <div className="relative h-[55px] w-[55px]">
        <div className="absolute left-1/2 top-1/2 [transform:translate(-50%,-50%)_scale(0.2)]">
          <div className="kt-bob kt-stage">
            <div className="kt-book relative h-[150px] w-[250px]">
              {/* Purple-to-maroon cover peeking out under the page block */}
              <div className="absolute -inset-x-2 -top-1.5 -bottom-4 rounded-lg bg-[linear-gradient(100deg,#6a3fa3,#9c3a62_55%,#b3405a)] shadow-[0_3px_0_#7a2a46,0_16px_26px_rgba(0,0,0,0.4)]" />
              {/* Fills the notch the two page stacks leave at the spine */}
              <div className="absolute left-1/2 top-full h-[13px] w-[8px] -translate-x-1/2 bg-[#ecd3a6]" />
              {/* Spine crease */}
              <div className="absolute inset-y-0 left-1/2 w-[2px] -translate-x-1/2 bg-[rgba(90,45,20,0.45)]" />
              {/* Static halves */}
              <div className="kt-page kt-static kt-left absolute inset-y-0 left-0 w-1/2 rounded-l-sm">
                {pageText(PAGES[0])}
              </div>
              <div className="kt-page kt-static kt-right absolute inset-y-0 right-0 w-1/2 rounded-r-sm">
                {pageText(PAGES[1])}
              </div>
              {/* Flipping pages */}
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="kt-leaf absolute inset-y-0 left-0 w-1/2"
                  style={{ animationDelay: `${i * 0.3}s` }}
                >
                  <div className="kt-page kt-left absolute inset-0 rounded-l-sm">
                    {pageText(PAGES[(2 + i * 2) % PAGES.length])}
                  </div>
                  <div className="kt-page kt-right kt-back absolute inset-0 rounded-r-sm">
                    {pageText(PAGES[(3 + i * 2) % PAGES.length])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div
        dir={i18n.dir(i18n.language)}
        className="mt-[10px] text-center text-base font-bold tracking-[1.5px] text-[var(--kol-text)]"
      >
        {message}
      </div>
      <style>{bookStyles}</style>
    </div>
  </div>
);

export const SpinnerOverlay: React.FC<SpinnerOverlayProps> = ({
  message = 'Logging in...',
  transparent = false,
}) => {
  if (typeof document === 'undefined') return null;
  return createPortal(overlayContent(message, transparent), document.body);
};

export default SpinnerOverlay;
